import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import { verify2FALogin, verify2FABackupCode } from "services/userApi";

const bgImage =
  "https://raw.githubusercontent.com/creativetimofficial/public-assets/master/argon-dashboard-pro/assets/img/signin-basic.jpg";

function TwoFactorVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const [verificationCode, setVerificationCode] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Get user data passed from sign-in page
  const { email, tempToken } = location.state || {};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      let response;
      
      if (useBackupCode) {
        // Verify with backup code
        if (!backupCode.trim()) {
          setError("Please enter a backup code");
          setLoading(false);
          return;
        }
        
        response = await verify2FABackupCode(tempToken, backupCode.trim());
      } else {
        // Verify with TOTP code
        if (verificationCode.length !== 6) {
          setError("Please enter a 6-digit code");
          setLoading(false);
          return;
        }
        
        response = await verify2FALogin(tempToken, verificationCode);
      }

      // Store auth token and navigate
      if (response.access_token) {
        localStorage.setItem('token', response.access_token);
        localStorage.setItem('user', JSON.stringify(response.user));
        navigate("/dashboard");
      }
    } catch (err) {
      setError(useBackupCode ? "Invalid backup code" : "Invalid verification code");
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    // This would typically trigger a new TOTP generation on authenticator app
    console.log("User should check their authenticator app for current code");
  };

  return (
    <CoverLayout
      title="Two-Factor Authentication"
      description="Enter your verification code to continue"
      image={bgImage}
    >
      <Card>
        <ArgonBox p={3} mb={1} textAlign="center">
          <ArgonTypography variant="h5" fontWeight="medium">
            Verify Your Identity
          </ArgonTypography>
          {email && (
            <ArgonTypography variant="body2" color="text" mt={1}>
              {email}
            </ArgonTypography>
          )}
        </ArgonBox>
        
        <ArgonBox pb={3} px={3}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          <ArgonBox component="form" role="form" onSubmit={handleSubmit}>
            {!useBackupCode ? (
              <>
                <ArgonTypography variant="body2" color="text" textAlign="center" mb={3}>
                  Enter the 6-digit code from your authenticator app
                </ArgonTypography>
                
                <ArgonBox mb={3}>
                  <ArgonInput
                    placeholder="000000"
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setVerificationCode(value);
                    }}
                    sx={{
                      "& input": { 
                        textAlign: "center", 
                        fontSize: "24px",
                        letterSpacing: "8px",
                        fontFamily: "monospace"
                      }
                    }}
                  />
                </ArgonBox>
              </>
            ) : (
              <>
                <ArgonTypography variant="body2" color="text" textAlign="center" mb={3}>
                  Enter one of your backup codes
                </ArgonTypography>
                
                <ArgonBox mb={3}>
                  <ArgonInput
                    placeholder="Enter backup code"
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value)}
                  />
                </ArgonBox>
              </>
            )}
            
            <ArgonBox mt={4} mb={1}>
              <ArgonButton
                variant="gradient"
                color="primary"
                fullWidth
                type="submit"
                disabled={loading}
              >
                {loading ? "Verifying..." : "Verify"}
              </ArgonButton>
            </ArgonBox>
            
            <ArgonBox mt={3} textAlign="center">
              <ArgonTypography variant="button" color="text" fontWeight="regular">
                {!useBackupCode ? (
                  <>
                    Lost your device?{" "}
                    <ArgonTypography
                      component="span"
                      variant="button"
                      color="primary"
                      fontWeight="medium"
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setUseBackupCode(true);
                        setError("");
                      }}
                    >
                      Use a backup code
                    </ArgonTypography>
                  </>
                ) : (
                  <>
                    Have your device?{" "}
                    <ArgonTypography
                      component="span"
                      variant="button"
                      color="primary"
                      fontWeight="medium"
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setUseBackupCode(false);
                        setError("");
                      }}
                    >
                      Use authenticator app
                    </ArgonTypography>
                  </>
                )}
              </ArgonTypography>
            </ArgonBox>
            
            <ArgonBox mt={2} textAlign="center">
              <ArgonTypography variant="button" color="text" fontWeight="regular">
                Need help?{" "}
                <ArgonTypography
                  component="a"
                  href="mailto:contact@prmcore.com"
                  variant="button"
                  color="primary"
                  fontWeight="medium"
                >
                  Contact Support
                </ArgonTypography>
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        </ArgonBox>
      </Card>
    </CoverLayout>
  );
}

export default TwoFactorVerify;